import { useContext, useEffect, useState } from "react";
import { Alert, Button, Form, Radio, Select, Tag, Tooltip, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";

/** Loads employees as select options for allotting selective holidays. */
export function useEmployeeOptions() {
    const { authData, backUrl } = useContext(AuthContext);
    const [employees, setEmployees] = useState([]);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get(`${backUrl}/api/emp/employees/mini_list/`, {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                });
                const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
                setEmployees(
                    list.map((e) => ({
                        value: e.id,
                        label: `${e.full_name || e.first_name} (${e.emp_code})`,
                    }))
                );
            } catch (err) {
                console.error("employee list error", err);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return employees;
}

/** "Applicable To" radio + employee multi-select for the add / edit holiday forms. */
export function AppliesToFields({ formInstance, employees }) {
    const { authData, backUrl } = useContext(AuthContext);
    const appliesToAll = Form.useWatch("applies_to_all", formInstance);
    const [uploading, setUploading] = useState(false);
    const [notFound, setNotFound] = useState([]);

    // Validates the emp_codes in the Excel against the employee table and adds the matches to the selection
    const handleExcel = async (file) => {
        const body = new FormData();
        body.append("file", file);
        setUploading(true);
        setNotFound([]);
        try {
            const res = await axios.post(`${backUrl}/api/hr/holidays/parse-employee-excel/`, body, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            const { matched, not_found } = res.data;
            const current = formInstance.getFieldValue("employee_ids") || [];
            formInstance.setFieldsValue({
                employee_ids: [...new Set([...current, ...matched.map((m) => m.id)])],
            });
            setNotFound(not_found);
            if (matched.length) toast.success(`${matched.length} employee(s) added from Excel`);
            if (not_found.length) toast.warn(`${not_found.length} employee code(s) not found`);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to read Excel file");
        } finally {
            setUploading(false);
        }
        return false;
    };
    return (
        <>
            <Form.Item name="applies_to_all" label="Applicable To" initialValue={true}>
                <Radio.Group>
                    <Radio value={true}>All Employees</Radio>
                    <Radio value={false}>Selected Employees</Radio>
                </Radio.Group>
            </Form.Item>
            {appliesToAll === false && (
                <Form.Item
                    name="employee_ids"
                    label="Select Employees"
                    rules={[{ required: true, message: "Select at least one employee" }]}
                >
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        placeholder="Search and select employees"
                        options={employees}
                        optionFilterProp="label"
                        maxTagCount="responsive"
                    />
                </Form.Item>
            )}
            {appliesToAll === false && (
                <div style={{ marginBottom: 16 }}>
                    <Upload accept=".xlsx" showUploadList={false} beforeUpload={handleExcel}>
                        <Button icon={<UploadOutlined />} loading={uploading}>
                            Upload Excel (emp_code)
                        </Button>
                    </Upload>
                    <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                        .xlsx with an "emp_code" column; codes are checked against the employee records.
                    </div>
                    {notFound.length > 0 && (
                        <Alert
                            style={{ marginTop: 8 }}
                            type="warning"
                            showIcon
                            message={`Not found (${notFound.length}): ${notFound.join(", ")}`}
                        />
                    )}
                </div>
            )}
        </>
    );
}

/** Table cell: shows "All Employees" or the selected-employee count (names on hover). */
export function AppliesToTag({ record }) {
    if (record.applies_to_all !== false) return <Tag color="blue">All Employees</Tag>;
    const emps = record.employee_details || [];
    return (
        <Tooltip title={emps.map((e) => `${e.name} (${e.emp_code})`).join(", ")}>
            <Tag color="orange">
                {emps.length} Selected Employee{emps.length === 1 ? "" : "s"}
            </Tag>
        </Tooltip>
    );
}

/** Fields to merge into the POST/PUT payload. */
export function appliesToPayload(values) {
    const all = values.applies_to_all !== false;
    return { applies_to_all: all, employee_ids: all ? [] : values.employee_ids || [] };
}

/** Initial form values from a holiday record. */
export function appliesToFormValues(record) {
    return {
        applies_to_all: record.applies_to_all !== false,
        employee_ids: (record.employee_details || []).map((e) => e.id),
    };
}
